import { LinkKind } from '../services/links-api.service';

/**
 * An invite or binding link the user opened, held across the sign-in redirect.
 *
 * The secret must survive a full page round trip through the identity provider,
 * and must NOT sit in the address bar while it does: it would land in history,
 * in referrers and in anything the user pastes. So it is read from the URL ONCE,
 * removed with history.replaceState at once, and kept in sessionStorage. That is
 * tab-scoped and gone when the tab closes, which is the right lifetime for a
 * single-use secret.
 */
export interface PendingLink {
  kind: LinkKind;
  customerId: string;
  linkId: string;
  secret: string;
}

export const PENDING_LINK_KEY = 'vericurePendingLink';

/** The landing route for each kind of link. */
export function landingPathFor(_kind: LinkKind): string {
  // Binding links are retired; an invite is the only link left.
  return '/invite';
}

/**
 * Read c / i|l / s from the current URL, store them, and strip them from the
 * address bar. Returns the link, or the one already stored when the URL carries
 * none (the return leg of the sign-in redirect).
 */
export function capturePendingLink(kind: LinkKind, win: Window = window): PendingLink | null {
  const url = new URL(win.location.href);
  const idParam = kind === 'invite' ? 'i' : 'l';
  const c = url.searchParams.get('c');
  const id = url.searchParams.get(idParam);
  const s = url.searchParams.get('s');
  if (c || id || s) {
    // Remove them FIRST, whatever happens next.
    ['c', 'i', 'l', 's'].forEach((p) => url.searchParams.delete(p));
    win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
    if (c && id && s) {
      const link: PendingLink = { kind, customerId: c, linkId: id, secret: s };
      savePendingLink(link, win);
      return link;
    }
    return null; // a partial link is not a link
  }
  const stored = loadPendingLink(win);
  return stored && stored.kind === kind ? stored : null;
}

export function savePendingLink(link: PendingLink, win: Window = window): void {
  try {
    win.sessionStorage.setItem(PENDING_LINK_KEY, JSON.stringify(link));
  } catch {
    /* storage blocked: the link still works within this page load */
  }
}

export function loadPendingLink(win: Window = window): PendingLink | null {
  try {
    const raw = win.sessionStorage.getItem(PENDING_LINK_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingLink;
    // A stored BINDING link (from before they were retired) is not a link any more.
    return p && p.kind === 'invite' && p.customerId && p.linkId && p.secret ? p : null;
  } catch {
    return null;
  }
}

/** Forget the link once it is used, refused for good, or abandoned. */
export function clearPendingLink(win: Window = window): void {
  try {
    win.sessionStorage.removeItem(PENDING_LINK_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Set when a sign-in is STARTED from the invite page for one link; read (and cleared) once when
 * the page loads again. Only that sign-in may accept the invite without asking (Joel, 2026-10-08).
 * A session that was already in the browser (a staff account in the same Safari private window,
 * say) is never taken as the invitee. sessionStorage, link id only: no secret, no identity.
 */
export const INVITE_SIGNIN_STARTED_KEY = 'vericureInviteSignInStarted';

export function markSignInStarted(linkId: string, win: Window = window): void {
  try {
    win.sessionStorage.setItem(INVITE_SIGNIN_STARTED_KEY, linkId);
  } catch {
    /* storage blocked: the page will ask which account instead of accepting outright */
  }
}

/** True once, and only for the link the sign-in was started for. Always clears the marker. */
export function takeSignInStarted(linkId: string, win: Window = window): boolean {
  try {
    const started = win.sessionStorage.getItem(INVITE_SIGNIN_STARTED_KEY);
    win.sessionStorage.removeItem(INVITE_SIGNIN_STARTED_KEY);
    return !!linkId && started === linkId;
  } catch {
    return false;
  }
}
